import {ICell, ICellMetricUOM, ICubeSource} from "./PA.HyperRectangle.IF";
import {BehaviorSubject} from "rxjs";
import {IDataRow} from "./CubeSource.IF";
import * as _ from "lodash"
import {IUOMConvertOptions, UOM} from "../PA.MD.Services/PA.MD.Services.IF";

interface CellKeyDict {[dimType: string]:  string}
interface CellRowDict {[aliasId: string]: {
    DataRow: IDataRow,
    UOMConvertOptions: IUOMConvertOptions,
    bIntegrationIndicator: boolean
    }}

export class CubeSourceCell
    implements ICell
{
    private _onCellChange: BehaviorSubject<{ Cell: ICell; MetricIds?: string[] }>
    private _cubeSource: ICubeSource
    private _cellId: string
    private _dimTypes: string[]
    private _dimKeys: (string | number)[]
    private _dctKeys: CellKeyDict
    private _dctCellRowByAlias: CellRowDict
    private _bMetricValuesLoaded: boolean
    private _dctUOMByMetric: {[metricId:string]: ICellMetricUOM}
    private _uomConvertOptions: IUOMConvertOptions

    constructor(cubeSource:  ICubeSource,
                cellId:      string,
                arrDimTypes: string[],
                arrDimKeys:  (string| number)[])
    {
        this._cubeSource = cubeSource
        this._cellId = cellId
        this._dimTypes = _.clone(arrDimTypes)
        this._dimKeys = _.clone(arrDimKeys)
        this._onCellChange = new BehaviorSubject<{ Cell: ICell; MetricIds?: string[] }>(undefined)
        this._bMetricValuesLoaded = true
        this._dctCellRowByAlias = {}
        this._dctKeys = {}
        if(arrDimKeys.length != arrDimTypes.length){
            console.log("CubeSourceCell key mismatch")
        }
        _.forEach(arrDimTypes, (dimType, idx) => {
            this._dctKeys[dimType] = <string>arrDimKeys[idx]
        })
        this._dctUOMByMetric = {}
        this._uomConvertOptions = {
            cubeSourceCell: this
        }

    }

    //<editor-fold desc="ICell operations">

    get CellId(): string { return this._cellId }
    get bCellEnabled(): boolean { return true };

    getKey(dimensionTypeId: string): string { return this._dctKeys[dimensionTypeId] || "" }
    getKeys(): { [p: string]: string } { return _.clone(this._dctKeys); }

    get bMetricValuesLoaded(): boolean {  return this._bMetricValuesLoaded }
    refreshMetricValues(metricIds?: string[]): Promise<void> | void {
        //-- Nothing to do here
        this._bMetricValuesLoaded = true
        return;
    }

    getCellMetricValue(metricId: string): string | number {
        let metric = this._cubeSource.getMetric(metricId)
        if (_.isEmpty(metric)) {
            console.log("Unknown Metric: ", metricId)
            return 0
        }

        let alias = metric.Options?.aliasId
        let row = this._dctCellRowByAlias[alias]

        if(row && row.DataRow)
            return row.DataRow[metric.MetricFieldName]

        return 0
    }

    getCellMetricUOM(metricId: string): ICellMetricUOM {
        return this._dctUOMByMetric[metricId];
    }

    getUOMConvertOptions(MetricId: string): IUOMConvertOptions {
        let metric = this._cubeSource.getMetric(MetricId)
        if(_.isEmpty(metric)){
            console.log("Unknown Metric: ", MetricId)
            return
        }

        let alias = metric.Options?.aliasId
        let row = this._dctCellRowByAlias[alias]

        if(row)
            return (row.UOMConvertOptions || this._uomConvertOptions)

        return this._uomConvertOptions

    }


    setCellMetricValue(MetricId: string, value: number | string): void {
        let metric = this._cubeSource.getMetric(MetricId)
        if(_.isEmpty(metric)){
            console.log("Unknown Metric: ", MetricId)
            return
        }

        if(metric.bIsReadOnly && !metric.bIsWriteBackEnabled)
            return

        let newValue = _.toNumber(value)
        if(!_.isNumber(newValue))
            return

        let alias = metric.Options?.aliasId
        let row = this._dctCellRowByAlias[alias]

        if(row && row.DataRow)
            row.DataRow[metric.MetricFieldName] = newValue

        this.OnCellChange.next({Cell: this, MetricIds: [MetricId]})

    }

    get OnCellChange(): BehaviorSubject<{ Cell: ICell; MetricIds?: string[] }> { return this._onCellChange }
    //</editor-fold>

    //<editor-fold desc="Construction/ maintenance operations">

    setCellMetricUOM(metricId: string, uom: ICellMetricUOM): void {
        this._dctUOMByMetric[metricId] = uom
    }

    setDataRow(aliasId: string, dataRow: IDataRow): boolean {
        if(!(aliasId in this._dctCellRowByAlias)){
            this._dctCellRowByAlias[aliasId] = {
                DataRow: dataRow,
                UOMConvertOptions: {dataRow: dataRow },
                bIntegrationIndicator: false
            }
            return true
        } else {
            let cr = this._dctCellRowByAlias[aliasId]
            cr.bIntegrationIndicator = false
            if(dataRow !== cr.DataRow){
                cr.DataRow = dataRow
                cr.UOMConvertOptions.dataRow = dataRow
                return true
            }
            return false
        }
    }

    getDataRow(aliasId: string): IDataRow {
        if(aliasId in this._dctCellRowByAlias)
            return this._dctCellRowByAlias[aliasId].DataRow
        return undefined
    }

    setConvertOptions(uomConvertOptions: IUOMConvertOptions){
        this._uomConvertOptions = _.clone(uomConvertOptions || {})
        this._uomConvertOptions.cubeSourceCell = this

        _.forEach(this._dctCellRowByAlias, (value, alias) => {
            value.UOMConvertOptions = _.clone(this._uomConvertOptions)
            value.UOMConvertOptions.dataRow = value.DataRow
        })
    }

    setIntegrationMarkers(){
        _.forEach(this._dctCellRowByAlias, (value, alias) => {
            value.bIntegrationIndicator = true
        })
    }

    purgeRowsWithIntegrationMarker(){
        let keysToRemove: string[] = []

        _.forEach(this._dctCellRowByAlias, (value, alias) => {
            if(value.bIntegrationIndicator)
                keysToRemove.push(alias)
        })
        _.forEach(keysToRemove, (alias) => {
            delete this._dctCellRowByAlias[alias]
        })
    }

    rowCount(): number {
        return _.keys(this._dctCellRowByAlias).length
    }


    //</editor-fold>
}